import { useId, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchScheduleRouteOptions, type ScheduleRouteOption } from "../data/routes";
import {
  createShiftTemplate,
  discardShiftTemplate,
  EMPTY_TEMPLATE_COLOR,
  EMPTY_TEMPLATE_OPEN_FILL_MODE,
  fetchShiftTemplates,
  retireShiftTemplate,
  SAFE_TEMPLATE_DISCARD_FAILURE,
  SAFE_TEMPLATE_RETIRE_FAILURE,
  SAFE_TEMPLATE_WRITE_FAILURE,
  SCHEDULE_COLORS_UNAVAILABLE,
  updateShiftTemplate,
  type ShiftTemplateDraft,
  type ShiftTemplateRow,
} from "../data/schedule-templates";
import { TemplateColorChip } from "../components/TemplateColorChip";
import { GlassModal } from "../components/GlassModal";
import { RowMenu, rowMenuItems } from "../components/RowMenu";
import { OPEN_FILL_MODE_COPY } from "../../../../packages/shared/src/schedule-color.ts";
import { useReadModel } from "../data/useReadModel";
import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import { TemplateDraftFields, MODE_FLIP_WARNING } from "./TemplateDraftFields";
import { DEFAULT_SHIFT_DURATION_MINUTES } from "../lib/shift-duration";
import {
  draftsSavedThisSession,
  markDraftSavedThisSession,
} from "../lib/schedule-wizard-session";
import { draftResumeCue } from "../lib/schedule-wizard-model";
import { scrollToPageSection } from "../lib/scroll-to-section";
import { DEFAULT_SITE_TIMEZONE } from "../lib/timezone-options";

// Test compatibility and single-source copy: the warning moved with the fields it guards.
export { MODE_FLIP_WARNING };

/** Jump to Builder (step 2) so drafts make the next action obvious. */
function goToBuilderToPublish(): void {
  scrollToPageSection("schedule-builder", "schedule-page__section--pulse");
}

// Template list + live create/edit (CONSOLE-SCHEDULE-TEMPLATE-LIST-SCAFFOLD + CREATE/
// EDIT-LIVE). Shows what the scheduling system expands each week: name, route/site (labels
// labels joined from sites + routes (active routes only; not rollout-gated),
// weekdays in the Postgres dow convention, local start + duration, headcount, status,
// active window, and required credential chips. Writes are manager-only, explicit-submit,
// DRAFT-only (published templates keep a disabled Edit - the update RPC refuses them
// anyway), no publish side effects, no slot creation; templates refetch after success.
// Without canManage the panel is read-only with zero write affordances (test-pinned).
//
// 2026-08-15 lock adds two lifecycle actions on the same rows, both confirm-gated:
// Discard draft (hard delete, draft rows only, refused server-side once anything
// references the template) and Retire (published rows only, soft flip to `retired`,
// slots untouched). The default read stays draft + published, so the same reload that
// follows a discard also makes a freshly retired row leave the list.
//
// Console shell v3 lock 8 (Call 4, 2026-08-15): retired templates stay reachable here
// behind an "Include retired" filter, OFF by default, because invisible history reads as
// deletion. Retired rows are read only - no edit, no lifecycle actions, no publish cue -
// and the Builder picker and publish path never see them regardless of the filter.

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export function weekdayLabels(weekdays: readonly number[]): string {
  if (weekdays.length === 7) {
    return "Every day";
  }
  return [...weekdays]
    .sort((a, b) => a - b)
    .map((day) => WEEKDAY_SHORT[day] ?? `Day ${day}`)
    .join(", ");
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

interface TemplatePanelData {
  templates: readonly ShiftTemplateRow[];
  routes: ScheduleRouteOption[];
  routesById: Map<string, ScheduleRouteOption>;
  /** Server predates Wave 6A: colors read slate, modes read instant claim. */
  colorsDegraded: boolean;
}

/** null = form closed; mode create has no template. */
interface FormTarget {
  mode: "create" | "edit";
  template: ShiftTemplateRow | null;
}

/**
 * Lifecycle confirm target (2026-08-15 product lock, decisions 1 and 2).
 *
 * `discard` is a HARD DELETE of a never-published draft and is offered on draft rows
 * only. `retire` is a soft flip to `retired` that leaves every published slot on the
 * calendar and is offered on published rows only. Both are `schedule.manage`-gated
 * server-side and confirm-gated here; neither is offered without `canManage`. There is
 * no unretire in this lock, so a retired row simply leaves the list.
 */
interface LifecycleTarget {
  kind: "discard" | "retire";
  template: ShiftTemplateRow;
}

/**
 * What Schedule now hands the connected flow. Deliberately the minimum a resume needs
 * (which pattern, and its name for the chrome) so the CREATE path can produce one too:
 * `createShiftTemplate` returns the new id, and a freshly saved draft is exactly the
 * case board frame 06 is about.
 */
export interface ScheduleResumeTarget {
  readonly templateId: string;
  readonly name: string;
}

export const DISCARD_DRAFT_WARNING =
  "This draft has never been published, so nothing on the calendar changes. The draft itself is deleted for good - this cannot be undone.";

export const RETIRE_TEMPLATE_WARNING =
  "Shifts already published from this template stay on the calendar exactly as they are. Retiring only stops new weeks being published from it, and takes it out of the template pickers. There is no undo yet.";

export function TemplateList({
  calendarMode = false,
  client,
  accountId,
  canManage = false,
  onShowBuilder,
  onTemplatesChanged,
  onScheduleTemplate,
  onScheduleShift,
}: {
  /** Names the actual Calendar destination; legacy embeds retain Builder labels. */
  calendarMode?: boolean;
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage - write affordances only exist when true (fail closed). */
  canManage?: boolean;
  /**
   * Shell v3 workbench: the Schedule page switches subrail sections instead of scrolling,
   * so the "Publish in Builder" cues ask the page to show the Builder section. Without it
   * (tests, legacy embeds) the cue falls back to the in-page scroll jump.
   */
  onShowBuilder?: () => void;
  /**
   * Fires after any template mutation lands (create, edit, discard, retire), alongside
   * this list's own refetch, so the page-level metrics strip never contradicts the list
   * sitting directly under it.
   */
  onTemplatesChanged?: () => void;
  /**
   * Board rule 5 / frame 06: resume a saved draft in the connected flow at Choose week.
   * Absent until that flow can accept a pattern, and while absent no row pretends
   * otherwise - an entry point that lands somewhere it should not is worse than none.
   */
  onScheduleTemplate?: ((target: ScheduleResumeTarget) => void) | undefined;
  /**
   * Slice C: the hub CTA enters the connected wizard at Define. Absent (tests, legacy
   * embeds) the CTA falls back to the drawer form, which writes through the same hosts.
   */
  onScheduleShift?: (() => void) | undefined;
}) {
  // Console shell v3 lock 8: OFF by default; flipping it widens the read to retired rows.
  const [includeRetired, setIncludeRetired] = useState(false);
  const { status, data, error, reload } = useReadModel<TemplatePanelData>(
    async () => {
      const [templateLoad, routes] = await Promise.all([
        fetchShiftTemplates(client, accountId, { includeRetired }),
        fetchScheduleRouteOptions(client, accountId),
      ]);
      return {
        templates: templateLoad.templates,
        colorsDegraded: templateLoad.degraded,
        routes,
        routesById: new Map(routes.map((route) => [route.routeId, route])),
      };
    },
    [client, accountId, includeRetired],
  );
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);
  const [lifecycleTarget, setLifecycleTarget] = useState<LifecycleTarget | null>(null);
  // Lifted so the modal itself can refuse backdrop/Escape dismissal while a discard or
  // retire is in flight - a hard delete must not be able to land with its receipt
  // dismissed out from under the operator.
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading templates...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load shift templates."}
      </p>
    );
  }

  const { templates, routes, routesById, colorsDegraded } = data ?? {
    templates: [],
    routes: [],
    routesById: new Map<string, ScheduleRouteOption>(),
    colorsDegraded: false,
  };
  // Belt and braces with the read: even if a wider read hands back retired rows, they only
  // render while the filter is on.
  const visibleTemplates = includeRetired
    ? templates
    : templates.filter((template) => template.status !== "retired");
  const showBuilder = onShowBuilder ?? goToBuilderToPublish;
  const reloadAfterMutation = () => {
    reload();
    onTemplatesChanged?.();
  };
  return (
    <div className="tpl">
      {colorsDegraded ? (
        <p className="surface__state" role="status">
          {SCHEDULE_COLORS_UNAVAILABLE}
        </p>
      ) : null}
      <div className="tpl__actions">
        {canManage ? (
          <button
            type="button"
            className="minter__confirm-go"
            onClick={
              onScheduleShift ?? (() => setFormTarget({ mode: "create", template: null }))
            }
          >
            Schedule a shift
          </button>
        ) : (
          <button type="button" className="builder__pending" disabled title="Requires schedule manage permission">
            Schedule a shift
          </button>
        )}
        <label className="tpl__retired-filter">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
          />
          Include retired
        </label>
      </div>
      {visibleTemplates.length === 0 ? (
        <p className="surface__state" role="status">
          No shift patterns yet. A pattern is the repeating shift a week gets published from
          {canManage ? " - schedule the first one above" : ""}.
        </p>
      ) : (
        <ul className="tpl__list" aria-label="Shift patterns">
          {visibleTemplates.map((template) => {
            const route = template.routeId !== null ? routesById.get(template.routeId) : undefined;
            const retired = template.status === "retired";
            const editable = canManage && template.status === "draft";
            return (
              <li
                key={template.templateId}
                className={`tpl__item${
                  formTarget?.mode === "edit" && formTarget.template?.templateId === template.templateId
                    ? " tpl__item--editing"
                    : ""
                }${retired ? " tpl__item--retired" : ""}`}
              >
                <div className="tpl__head">
                  {/* Template color: a chip beside the name, never the row's fill and never
                      inside the status pill's box - the two vocabularies stay separate. */}
                  <TemplateColorChip colorToken={template.colorToken} />
                  <span className="tpl__name">{template.name}</span>
                  <span
                    className={`schedule__chip schedule__chip--${
                      template.status === "published" ? "filled" : retired ? "retired" : "draft"
                    }`}
                  >
                    {template.status === "draft" ? "Draft" : humanizeLabel(template.status)}
                  </span>
                  {template.openFillMode === "request_approval" ? (
                    <span className="schedule__tag">{OPEN_FILL_MODE_COPY.request_approval}</span>
                  ) : null}
                  {retired ? (
                    /* Lock 8: retired rows are history, not workspace. No edit, no lifecycle
                       verbs, no publish cue - the row states its own posture instead. */
                    <span className="tpl__head-actions tpl__retired-note">
                      History kept - read only
                    </span>
                  ) : (
                  /* Actions trail as one cluster so Draft rows stay the same height/alignment as
                      Published (Publish cue no longer steals a second margin-left:auto lane). */
                  <div className="tpl__head-actions">
                    {editable ? (
                      <button
                        type="button"
                        className="builder__navbtn tpl__edit"
                        onClick={() => setFormTarget({ mode: "edit", template })}
                      >
                        Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="builder__pending tpl__edit"
                        disabled
                        title={
                          canManage
                            ? "Only draft templates can be edited - create a new draft instead"
                            : "Requires schedule manage permission"
                        }
                      >
                        Edit
                      </button>
                    )}
                    {template.status === "draft" && canManage ? (
                      onScheduleTemplate !== undefined ? (
                        (() => {
                          // Board rule 5 / frame 06: the draft row's cue is Schedule now,
                          // resuming the wizard at Choose week. A draft saved in THIS
                          // session resumes loudly; older drafts resume quietly so the
                          // hub keeps one loud control (Call 4).
                          const cue = draftResumeCue({
                            template,
                            savedThisSession: draftsSavedThisSession(),
                          });
                          return cue !== null ? (
                            <button
                              type="button"
                              className={
                                cue.tone === "primary"
                                  ? "minter__confirm-go tpl__schedule-cue"
                                  : "builder__navbtn tpl__schedule-cue"
                              }
                              onClick={() =>
                                onScheduleTemplate({
                                  templateId: template.templateId,
                                  name: template.name,
                                })
                              }
                            >
                              {cue.label}
                            </button>
                          ) : null;
                        })()
                      ) : (
                        // Legacy embeds without the wizard seam keep the honest old cue
                        // rather than pretending a wizard exists to resume into.
                        <button
                          type="button"
                          className="tpl__publish-cue"
                          onClick={showBuilder}
                          title="Drafts are not on the calendar until you publish a week from the Builder"
                        >
                          {calendarMode ? "Publish in Calendar" : "Publish in Builder"}
                        </button>
                      )
                    ) : null}
                    {/* Wave 2 board rule 6, lifecycle seating: Discard and Retire keep every
                        confirm, refusal rule, and audit event from the 2026-08-15 lock and
                        only change ADDRESS - overflow menu, not headline chrome - so each
                        row carries at most one loud control. Discard belongs to drafts,
                        Retire to published rows, never both, neither without
                        schedule.manage. Published rows additionally seat the board's two
                        quiet verbs here: Schedule this pattern reruns the wizard at Choose
                        week, View in Builder opens the power surface. */}
                    <RowMenu
                      ariaLabel={`Actions for ${template.name}`}
                      items={rowMenuItems([
                        canManage &&
                        template.status === "published" &&
                        onScheduleTemplate !== undefined
                          ? {
                              key: "schedule",
                              label: "Schedule this pattern",
                              help: "Publish another week of this pattern.",
                              onSelect: () =>
                                onScheduleTemplate({
                                  templateId: template.templateId,
                                  name: template.name,
                                }),
                            }
                          : null,
                        canManage && template.status === "published"
                          ? {
                              key: "builder",
                              label: calendarMode ? "View in Calendar" : "View in Builder",
                              help: "Assign officers, copy weeks, or cancel slots.",
                              onSelect: showBuilder,
                            }
                          : null,
                        canManage && template.status === "draft"
                          ? {
                              key: "discard",
                              label: "Discard draft",
                              destructive: true,
                              help: "Hard delete of this never-published draft. This cannot be undone.",
                              onSelect: () => setLifecycleTarget({ kind: "discard", template }),
                            }
                          : null,
                        canManage && template.status === "published"
                          ? {
                              key: "retire",
                              label: "Retire pattern",
                              destructive: true,
                              help: "Existing shifts stay. The pattern is no longer offered for new publishes.",
                              onSelect: () => setLifecycleTarget({ kind: "retire", template }),
                            }
                          : null,
                      ])}
                    />
                  </div>
                  )}
                </div>
                <p className="tpl__route">
                  {route !== undefined
                    ? `${route.routeName} - ${route.siteName}`
                    : "Route not found"}
                </p>
                <p className="tpl__meta">
                  <span>{weekdayLabels(template.weekdays)}</span>
                  <span className="builder__window">
                    {template.localStartTime.slice(0, 5)} - {formatDuration(template.durationMinutes)}
                  </span>
                  <span>
                    {template.headcountRequired} guard{template.headcountRequired === 1 ? "" : "s"}
                  </span>
                  <span className="tpl__window">
                    {template.activeFrom ?? "open start"} to {template.activeUntil ?? "open end"}
                  </span>
                </p>
                {template.requiredCredentialTypes.length > 0 ? (
                  <p className="tpl__credentials">
                    {template.requiredCredentialTypes.map((credential) => (
                      <span key={credential} className="schedule__chip schedule__chip--credential">
                        {humanizeLabel(credential)}
                      </span>
                    ))}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {canManage && formTarget !== null ? (
        <GlassDrawer
          open
          size="xl"
          ariaLabel={
            formTarget.mode === "create"
              ? "New shift template"
              : `Edit template - ${formTarget.template?.name ?? ""}`
          }
          onClose={() => setFormTarget(null)}
        >
          <TemplateForm
            client={client}
            accountId={accountId}
            routes={routes}
            target={formTarget}
            onClose={() => setFormTarget(null)}
            onSaved={reloadAfterMutation}
            onShowBuilder={showBuilder}
            onScheduleTemplate={onScheduleTemplate}
          />
        </GlassDrawer>
      ) : null}
      {canManage && lifecycleTarget !== null ? (
        <GlassModal
          open
          busy={lifecycleBusy}
          ariaLabel={
            lifecycleTarget.kind === "discard"
              ? `Discard draft - ${lifecycleTarget.template.name}`
              : `Retire template - ${lifecycleTarget.template.name}`
          }
          onClose={() => {
            if (lifecycleBusy) return;
            setLifecycleTarget(null);
          }}
        >
          <TemplateLifecycleConfirm
            key={`${lifecycleTarget.kind}-${lifecycleTarget.template.templateId}`}
            client={client}
            accountId={accountId}
            target={lifecycleTarget}
            onBusyChange={setLifecycleBusy}
            onClose={() => {
              setLifecycleBusy(false);
              setLifecycleTarget(null);
            }}
            onDone={reloadAfterMutation}
          />
        </GlassModal>
      ) : null}
    </div>
  );
}

type LifecycleOutcome = { ok: true; note: string } | { ok: false; reason: string };

/**
 * One confirm body for both lifecycle actions. Nothing runs until the operator presses
 * the explicit confirm button (test-pinned: zero rpc calls on open), the confirm is
 * replaced by Close once the write lands, and the list reloads behind the receipt so a
 * discarded draft disappears and a retired template drops out of the draft+published read.
 */
function TemplateLifecycleConfirm({
  client,
  accountId,
  target,
  onBusyChange,
  onClose,
  onDone,
}: {
  client: SupabaseClient;
  accountId: string;
  target: LifecycleTarget;
  /** Lifts the in-flight flag so the modal can refuse dismissal mid-write. */
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<LifecycleOutcome | null>(null);
  const discarding = target.kind === "discard";

  function markBusy(next: boolean): void {
    setBusy(next);
    onBusyChange(next);
  }

  async function confirm(): Promise<void> {
    if (busy) return;
    markBusy(true);
    try {
      if (discarding) {
        const result = await discardShiftTemplate(client, accountId, target.template.templateId);
        if (result.ok) {
          setOutcome({ ok: true, note: "Draft discarded." });
          onDone();
        } else {
          setOutcome({ ok: false, reason: result.reason });
        }
      } else {
        const result = await retireShiftTemplate(client, accountId, target.template.templateId);
        if (result.ok) {
          setOutcome({
            ok: true,
            note:
              result.retainedSlotCount > 0
                ? `Template retired. ${result.retainedSlotCount} shift${
                    result.retainedSlotCount === 1 ? "" : "s"
                  } stay on the calendar.`
                : "Template retired.",
          });
          onDone();
        } else {
          setOutcome({ ok: false, reason: result.reason });
        }
      }
    } catch {
      setOutcome({
        ok: false,
        reason: discarding ? SAFE_TEMPLATE_DISCARD_FAILURE : SAFE_TEMPLATE_RETIRE_FAILURE,
      });
    } finally {
      markBusy(false);
    }
  }

  return (
    <div className="minter__confirm">
      <h3 className="minter__confirm-title">
        {discarding ? "Discard draft" : "Retire template"} - {target.template.name}
      </h3>
      <p className="minter__confirm-warning">
        {discarding ? DISCARD_DRAFT_WARNING : RETIRE_TEMPLATE_WARNING}
      </p>
      {outcome !== null ? (
        outcome.ok ? (
          <p className="builder__drawer-ok" role="status">
            {outcome.note}
          </p>
        ) : (
          <p className="surface__state surface__state--error" role="alert">
            {outcome.reason}
          </p>
        )
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Close
        </button>
        {outcome === null || !outcome.ok ? (
          <button
            type="button"
            className="minter__confirm-go"
            onClick={() => void confirm()}
            disabled={busy}
          >
            {discarding ? "Confirm discard" : "Confirm retire"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

const DEFAULT_TIMEZONE = DEFAULT_SITE_TIMEZONE;

function draftFrom(target: FormTarget): ShiftTemplateDraft {
  const template = target.template;
  return {
    siteId: template?.siteId ?? "",
    routeId: template?.routeId ?? "",
    name: template?.name ?? "",
    siteTimezone: template?.siteTimezone ?? DEFAULT_TIMEZONE,
    localStartTime: template?.localStartTime.slice(0, 5) ?? "",
    durationMinutes: template?.durationMinutes ?? DEFAULT_SHIFT_DURATION_MINUTES,
    weekdays: template !== null ? [...template.weekdays] : [],
    activeFrom: template?.activeFrom ?? "",
    activeUntil: template?.activeUntil ?? null,
    headcountRequired: template?.headcountRequired ?? 1,
    requiredCredentialTypes: template !== null ? [...template.requiredCredentialTypes] : [],
    colorToken: template?.colorToken ?? EMPTY_TEMPLATE_COLOR,
    openFillMode: template?.openFillMode ?? EMPTY_TEMPLATE_OPEN_FILL_MODE,
  };
}

/** Explicit-submit draft form, shared by create and edit. Drafts only - publishing is the Builder's job. */
function TemplateForm({
  client,
  accountId,
  routes,
  target,
  onClose,
  onSaved,
  onShowBuilder,
  onScheduleTemplate,
}: {
  client: SupabaseClient;
  accountId: string;
  routes: ScheduleRouteOption[];
  target: FormTarget;
  onClose: () => void;
  onSaved: () => void;
  /** Shows the Builder section (subrail switch on the workbench page, scroll fallback). */
  onShowBuilder: () => void;
  /**
   * Board frame 06 resume cue. When the connected flow can carry a pattern into Choose
   * week, this reopens that wizard; while it cannot, the surface says the honest thing
   * instead of routing a first publish through Builder under a wizard's name.
   */
  onScheduleTemplate?: ((target: ScheduleResumeTarget) => void) | undefined;
}) {
  const [draft, setDraft] = useState<ShiftTemplateDraft>(() => draftFrom(target));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [savedTarget, setSavedTarget] = useState<ScheduleResumeTarget | null>(null);
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();
  // Only an EDIT that moves an existing request-approval template back to instant claim can
  // strand pendings; a brand-new draft has none.
  const flippingToInstantClaim =
    target.mode === "edit" &&
    target.template?.openFillMode === "request_approval" &&
    draft.openFillMode === "instant_claim";

  function patch(partial: Partial<ShiftTemplateDraft>): void {
    setDraft((current) => ({ ...current, ...partial }));
  }

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const outcome =
        target.mode === "create"
          ? await createShiftTemplate(client, accountId, draft)
          : await updateShiftTemplate(client, accountId, target.template!.templateId, draft);
      if (outcome.ok) {
        // A brand-new draft has no row yet, so the resume target comes from the id the
        // create RPC just returned. Without this the create path could never offer
        // Schedule now, which is the exact case board frame 06 describes.
        setSavedTarget({ templateId: outcome.templateId, name: draft.name });
        // Board Call 4: a draft saved in this session resumes loudly on its list row.
        markDraftSavedThisSession(outcome.templateId);
        setSaved(true);
        onSaved();
      } else {
        setFailure(outcome.reason);
      }
    } catch {
      setFailure(SAFE_TEMPLATE_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={target.mode === "create" ? "New template" : `Edit template - ${target.template?.name ?? ""}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {/* Wave 2 language lock: the entry says Schedule a shift, so what it opens has to
          speak the same job. The old title taught the Templates-then-Builder architecture
          the board exists to remove, and named Builder as the next step in body copy. */}
      <h3 className="minter__confirm-title">
        {target.mode === "create"
          ? "Schedule a shift"
          : `Edit shift pattern - ${target.template?.name ?? ""}`}
      </h3>
      <p className="minter__confirm-note">
        Set the repeating pattern: days, hours, route, and how many officers. Nothing reaches
        the calendar until a week is published.
      </p>
      {saved ? (
        <div role="status">
          <p className="builder__drawer-ok">Draft saved.</p>
          {/* Board frame 06: Save draft is an exit, never a dead end. Once the connected
              flow supplies its step bodies, `onScheduleTemplate` resumes the wizard at
              Choose week; until then the honest next step is the week picker in Builder,
              stated as a follow-up action rather than as this surface's loud CTA. */}
          <p className="tpl__saved-next">Schedule it when you are ready.</p>
          <div className="minter__confirm-actions tpl__saved-actions">
            <button
              type="button"
              className="minter__confirm-go"
              onClick={() => {
                onClose();
                if (onScheduleTemplate !== undefined && savedTarget !== null) {
                  const resume = savedTarget;
                  window.requestAnimationFrame(() => onScheduleTemplate(resume));
                  return;
                }
                // After the drawer unmounts, bring the week picker into view.
                window.requestAnimationFrame(() => onShowBuilder());
              }}
            >
              {onScheduleTemplate !== undefined && savedTarget !== null
                ? "Schedule now"
                : "Choose the week in Builder"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <TemplateDraftFields
            draft={draft}
            routes={routes}
            busy={busy}
            onPatch={patch}
            showModeFlipWarning={flippingToInstantClaim}
          />
          {failure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {failure}
            </p>
          ) : null}
        </>
      )}
      {!saved ? (
        <GlassDrawerFooter>
          <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
            Close
          </button>
          <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
            {target.mode === "create" ? "Create draft" : "Save draft"}
          </button>
        </GlassDrawerFooter>
      ) : (
        <GlassDrawerFooter>
          <button type="button" className="builder__navbtn" onClick={onClose}>
            Close
          </button>
        </GlassDrawerFooter>
      )}
    </form>
  );
}
